import { ChangeDetectionStrategy, Component, ElementRef, computed, input, model, viewChildren } from '@angular/core';

import type { EsnVersionTab } from '../../../shared/types/domain';

/**
 * The version scope bar above the Spatial Hierarchy board: pick which deployed version
 * (Published, Staging, Testing) the board describes. Arrow keys, Home and End move between stages.
 */
@Component({
  selector: 'esn-version-tabs',
  templateUrl: './version-tabs.component.html',
  styleUrl: './version-tabs.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionTabsComponent {
  readonly tabs = input.required<readonly EsnVersionTab[]>();

  /**
   * Value of the selected stage; the first enabled stage when unset.
   */
  readonly value = model<string | null>(null);

  /**
   * Accessible name of the tab list.
   */
  readonly label = input('Version');

  /**
   * Id of the panel the tabs control (the board), for `aria-controls`.
   */
  readonly controls = input<string | null>(null);

  private readonly buttons = viewChildren<ElementRef<HTMLButtonElement>>('tabButton');

  protected readonly current = computed(() => {
    const tabs = this.tabs();
    const value = this.value();
    return tabs.some((t) => t.value === value && !t.disabled) ? value : (tabs.find((t) => !t.disabled)?.value ?? null);
  });

  protected select(tab: EsnVersionTab): void {
    if (!tab.disabled) this.value.set(tab.value);
  }

  protected onKeydown(event: KeyboardEvent, index: number): void {
    const enabled = this.tabs()
      .map((tab, i) => ({ tab, i }))
      .filter(({ tab }) => !tab.disabled);
    const at = enabled.findIndex(({ i }) => i === index);
    let next: number | undefined;
    if (event.key === 'ArrowRight') next = enabled[(at + 1) % enabled.length]?.i;
    else if (event.key === 'ArrowLeft') next = enabled[(at - 1 + enabled.length) % enabled.length]?.i;
    else if (event.key === 'Home') next = enabled[0]?.i;
    else if (event.key === 'End') next = enabled[enabled.length - 1]?.i;
    if (next === undefined) return;

    event.preventDefault();
    this.select(this.tabs()[next]);
    this.buttons()[next]?.nativeElement.focus();
  }
}
